import { Fragment, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import GetHeroIcon from "../../utils/getHeroIcon";
import { ClassValue } from "clsx";
import { cn } from "../../utils/cn";

interface Props {
  open: boolean;
  setOpen?: { (value: boolean): void };
  children:
    | React.ReactNode
    | string
    | JSX.Element
    | JSX.Element[]
    | (() => JSX.Element);
  width?: boolean;
  childDialogTitle?: string;
  titleClass?: ClassValue;
  dialogueClassName?: ClassValue;
  animate?: boolean;
  closeBtn?: boolean;
  onPanelContextMenu?: (e: React.MouseEvent) => void;
}

const Modal = ({
  open,
  setOpen,
  children,
  width = false,
  childDialogTitle,
  titleClass,
  dialogueClassName,
  animate = false,
  closeBtn = false,
  onPanelContextMenu
}: Props) => {
  const [dialogTitle, setDialogTitle] = useState("");
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const animateClassName = animate ? " animate-slideDown " : " ";
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        initialFocus={closeBtn ? undefined : closeButtonRef}
        onClose={(value) => {
          const active = document.activeElement;
          if (active && active.tagName === "IFRAME") {
            return;
          }
          setOpen && setOpen(value);
        }}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full justify-center p-4 text-center items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                onContextMenu={onPanelContextMenu}
                className={cn(
                  "relative transform rounded-lg bg-bkg p-4 lg:pb-4 lg:pt-5 text-left shadow-xl transition-all my-8 w-full lg:p-6 max-w-[90%]",
                  dialogueClassName,
                  animateClassName
                )}
              >
                {(childDialogTitle || !closeBtn) && (
                  <div className="flex justify-between  mb-2 ">
                    <div>
                      <h2
                        className={cn(
                          "font-semibold text-xl text-gray-900 dark:text-gray-50",
                          titleClass
                        )}
                      >
                        {childDialogTitle}
                      </h2>
                    </div>
                    {!closeBtn && (
                      <div data-testid="closeModalIcon">
                        <button
                          ref={closeButtonRef}
                          type="button"
                          aria-label="Close dialog"
                          onClick={() => {
                            setOpen && setOpen(false);
                          }}
                          className="flex-shrink-0 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                        >
                          <GetHeroIcon
                            name="XMarkIcon"
                            className="w-6 h-6 font-bold"
                            aria-hidden="true"
                          />
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <>{children}</>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default Modal;
